import { cva, type VariantProps } from 'class-variance-authority'
import { LoaderCircle } from 'lucide-react'
import { Slot } from 'radix-ui'
import type { ComponentProps, MouseEvent } from 'react'

import { cn } from '../lib/cn'

/*
 * Foco: o anel de 2 px petróleo vem da regra global :focus-visible do tema.
 * Movimento: só a pressão (transform), nunca cor animada nem `transition: all`.
 */
export const buttonVariants = cva(
  [
    'inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap select-none',
    'transition-transform duration-(--motion-fast) ease-entrance active:scale-[0.98]',
    'motion-reduce:transition-none motion-reduce:active:scale-100',
    'disabled:pointer-events-none disabled:opacity-50',
    'aria-busy:cursor-progress aria-busy:active:scale-100',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-petroleo text-on-petroleo hover:bg-petroleo/90',
        secondary: 'border border-border bg-surface text-ink hover:bg-surface-2',
        ghost: 'text-ink hover:bg-surface-2',
        danger: 'bg-framboesa text-on-framboesa hover:bg-framboesa/90',
      },
      // Abaixo de 640 px (D3): 40 px de altura, 44 no primário de tamanho normal.
      size: {
        sm: 'h-8 px-3 text-compact max-sm:h-10',
        md: 'h-9 px-4 text-body max-sm:h-10',
      },
    },
    compoundVariants: [{ variant: 'primary', size: 'md', className: 'max-sm:h-11' }],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
)

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  /** Aplica o visual ao filho (por exemplo, um link do router) em vez de renderizar `<button>`. */
  asChild?: boolean
  /**
   * Em andamento: mostra o indicador, marca `aria-busy` e ignora cliques (inclusive o envio do
   * formulário), sem perder o foco nem apagar o botão.
   */
  loading?: boolean
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  type = 'button',
  onClick,
  children,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size }), className)

  if (asChild) {
    return (
      <Slot.Root className={classes} onClick={onClick} {...props}>
        {children}
      </Slot.Root>
    )
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading) {
      event.preventDefault()
      return
    }
    onClick?.(event)
  }

  return (
    <button
      type={type}
      className={classes}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={handleClick}
      {...props}
    >
      {loading ? (
        <LoaderCircle aria-hidden className="animate-spin motion-reduce:animate-none" />
      ) : null}
      {children}
    </button>
  )
}
